import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Chip,
  Breadcrumbs,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Autocomplete,
  Checkbox
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  LocalActivity as LocalActivityIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { amenityAPI, amenityScheduleAPI, memberProfileAPI, coachingSessionAPI, groupPlayAPI, coachingProgramAPI } from '../../services/api';
import { formatDateForInput, parseDateOnlyString, isDateOnly, formatTimeString } from '../../utils/dateUtils';
import BookAmenityDialog from './BookAmenityDialog';
import ReserveAmenityDialog from './ReserveAmenityDialog';
import CreateCoachingSessionDialog from './CreateCoachingSessionDialog';
import { Amenity } from '../../types';
import { useAuth } from '../../context/AuthContext';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Types
interface BookingGuest {
  type: 'member' | 'guest';
  memberId: string | null;
  guest: {
    name: string | null;
    email: string | null;
  };
}

interface AmenitySlot {
  startTime: string;
  endTime: string;
  status: string;
  bookedBy?: string;
  bookedByEmail?: string;
  bookingId?: string;
  memberId?: string;
  pricingTier?: string;
  programName?: string;
  programInstanceId?: string;
  programInstanceStatus?: string;
  coachingSessionName?: string;
  coachingSessionId?: string; // ID of the referenced coaching session or group play instance
  coachingSessionStatus?: string;
  maintenanceDescription?: string;
  equipment?: Array<{ name: string; quantity: number }>;
  allBookings?: Array<{
    id: string;
    memberName: string;
    guests?: BookingGuest[];
    coachingSessionId?: string;
    coachingSessionName?: string;
    programInstanceId?: string;
    programName?: string;
  }>;
}

const CourtsCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  // State management
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableAmenities, setAvailableAmenities] = useState<Amenity[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<Amenity[]>([]);
  const [amenitySlots, setAmenitySlots] = useState<{ [amenityId: string]: AmenitySlot[] }>({});
  const [amenitySlotDurations, setAmenitySlotDurations] = useState<{ [amenityId: string]: number }>({});
  const [outOfScheduleBookings, setOutOfScheduleBookings] = useState<{ [amenityId: string]: AmenitySlot[] }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<string>('');
  const [guestMemberProfiles, setGuestMemberProfiles] = useState<{ [memberId: string]: any }>({});
  const [validEntityIds, setValidEntityIds] = useState<Set<string>>(new Set());
  const [groupPlayInstanceMap, setGroupPlayInstanceMap] = useState<{ [instanceId: string]: { name: string; coachName?: string } }>({});

  // Booking dialog state
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [bookingDialogAmenity, setBookingDialogAmenity] = useState<Amenity | null>(null);
  const [bookingDialogDate, setBookingDialogDate] = useState<Date | null>(null);
  const [bookingDialogSlot, setBookingDialogSlot] = useState<{ startTime: string; endTime: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Choice dialog state (for admin/coach)
  const [openChoiceDialog, setOpenChoiceDialog] = useState(false);
  const [choiceDialogAmenity, setChoiceDialogAmenity] = useState<Amenity | null>(null);
  const [choiceDialogDate, setChoiceDialogDate] = useState<Date | null>(null);
  const [choiceDialogSlot, setChoiceDialogSlot] = useState<{ startTime: string; endTime: string } | null>(null);

  // Reserve dialog state
  const [openReserveDialog, setOpenReserveDialog] = useState(false);
  const [reserveDialogAmenity, setReserveDialogAmenity] = useState<Amenity | null>(null);
  const [reserveDialogDate, setReserveDialogDate] = useState<Date | null>(null);
  const [reserveDialogSlot, setReserveDialogSlot] = useState<{ startTime: string; endTime: string } | null>(null);

  // Create coaching session dialog state
  const [openCreateSessionDialog, setOpenCreateSessionDialog] = useState(false);
  const [createSessionDialogAmenity, setCreateSessionDialogAmenity] = useState<Amenity | null>(null);
  const [createSessionDialogDate, setCreateSessionDialogDate] = useState<Date | null>(null);
  const [createSessionDialogSlot, setCreateSessionDialogSlot] = useState<{ startTime: string; endTime: string } | null>(null);

  // Search state
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchAmenities, setSearchAmenities] = useState<Amenity[]>([]);
  const [matchingBookingIds, setMatchingBookingIds] = useState<Set<string>>(new Set());
  const [searchResultsCount, setSearchResultsCount] = useState(0);

  // Read query parameters
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const dateParam = searchParams.get('date');
    const timeParam = searchParams.get('time');

    if (dateParam) {
      try {
        const newDate = parseDateOnlyString(dateParam);
        setSelectedDate(newDate);
      } catch (error) {
        console.error('Invalid date parameter:', dateParam);
      }
    }

    // Store the preferred time for later use (e.g., auto-scroll)
    if (timeParam) {
      // We can use this to scroll to the time after data loads
      setTimeout(() => {
        scrollToTime(timeParam);
      }, 1000);
    }
  }, [searchParams]);

  // Fetch amenities list when component mounts
  useEffect(() => {
    const typeParam = searchParams.get('type');
    fetchAmenities(typeParam);
  }, [searchParams]);

  // Fetch calendar data when dependencies change
  useEffect(() => {
    if (selectedAmenities.length > 0) {
      fetchCalendarData();
    }
  }, [selectedDate, selectedAmenities]);

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (selectedDate.toDateString() === new Date().toDateString()) {
      setTimeout(() => {
        scrollToCurrentTime();
      }, 500);
    }
  }, [amenitySlots, selectedDate]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only handle shortcuts when not in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key) {
        case 't':
        case 'T':
          goToToday();
          break;
        case 'j':
        case 'J':
          openDatePickerDialog();
          break;
        case 'ArrowLeft':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            navigateDate('prev');
          }
          break;
        case 'ArrowRight':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            navigateDate('next');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [selectedDate]);

  const fetchAmenities = async (courtType?: string | null) => {
    try {
      const response = await amenityAPI.getAmenities();
      let amenities = response.data || [];
      
      // Filter by type if provided in query params
      if (courtType) {
        const courtTypeLower = courtType.toLowerCase();
        amenities = amenities.filter((a: Amenity) => 
          a.type?.toLowerCase().includes(courtTypeLower) || 
          a.name?.toLowerCase().includes(courtTypeLower)
        );
      }
      
      setAvailableAmenities(amenities);
      
      // Try to load saved amenity selection from localStorage
      const tenantId = localStorage.getItem('tenantId');
      const storageKey = `courtsCalendar_selectedAmenities_${tenantId}`;
      const savedSelection = localStorage.getItem(storageKey);
      
      let selectedAmenities: Amenity[] = [];
      
      // If court type is specified, select all matching amenities
      if (courtType && amenities.length > 0) {
        selectedAmenities = amenities;
      } else if (savedSelection) {
        try {
          const savedIds = JSON.parse(savedSelection) as string[];
          // Filter amenities to only include valid saved IDs that still exist
          selectedAmenities = amenities.filter((a: Amenity) => savedIds.includes(a.id));
        } catch (parseError) {
          console.error('Error parsing saved amenity selection:', parseError);
        }
      }
      
      // If no valid saved selection, default to first 3-4 amenities
      if (selectedAmenities.length === 0) {
        selectedAmenities = amenities.slice(0, Math.min(4, amenities.length));
      }
      
      setSelectedAmenities(selectedAmenities);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      setError('Failed to fetch amenities');
    }
  };

  const fetchGuestMemberProfiles = async (slotsData: { [amenityId: string]: AmenitySlot[] }) => {
    // Collect all unique member IDs from guests across all slots
    const memberIds = new Set<string>();
    Object.values(slotsData).forEach(slots => {
      slots.forEach(slot => {
        if (slot.allBookings) {
          slot.allBookings.forEach(booking => {
            if (booking.guests) {
              booking.guests.forEach(guest => {
                if (guest.type === 'member' && guest.memberId) {
                  memberIds.add(guest.memberId);
                }
              });
            }
          });
        }
      });
    });

    // Fetch profiles for all member guests
    const profiles: { [memberId: string]: any } = {};
    await Promise.all(
      Array.from(memberIds).map(async (memberId) => {
        try {
          const response = await memberProfileAPI.getMemberProfileById(memberId);
          profiles[memberId] = response.data;
        } catch (error) {
          console.error(`Error fetching member profile ${memberId}:`, error);
        }
      })
    );
    
    setGuestMemberProfiles(profiles);
    return profiles; // Return the profiles so they can be used immediately
  };

  const fetchValidEntityIds = async (dateString: string) => {
    const validIds = new Set<string>();
    const groupPlayMap: { [instanceId: string]: { name: string; coachName?: string } } = {};

    try {
      // Fetch sessions, group plays (with instances), and programs (with instances) in parallel
      await Promise.all([
        // 1. Fetch coaching sessions
        coachingSessionAPI.getCoachingSessions({ startDate: dateString, endDate: dateString })
          .then((sessionsResponse) => {
            (sessionsResponse.data || []).forEach((session: any) => {
              if (session.status !== 'CANCELLED') validIds.add(session.id);
            });
          })
          .catch((error) => console.error('Error fetching coaching sessions:', error)),

        // 2. Fetch group plays then their instances in parallel
        groupPlayAPI.getGroupPlays({ startDate: dateString, endDate: dateString })
          .then(async (groupPlaysResponse) => {
            const groupPlays = groupPlaysResponse.data || [];
            await Promise.all(
              groupPlays.map(async (groupPlay: any) => {
                try {
                  const instancesResponse = await groupPlayAPI.getGroupPlayInstances(groupPlay.id, {
                    startDate: dateString,
                    endDate: dateString
                  });
                  const coachDisplayName: string | undefined =
                    groupPlay.isGuestCoach ? (groupPlay.guestCoachName || undefined) : (groupPlay.coachName || undefined);
                  (instancesResponse.data || []).forEach((instance: any) => {
                    if (instance.status !== 'CANCELLED') validIds.add(instance.id);
                    groupPlayMap[instance.id] = { name: groupPlay.name, coachName: coachDisplayName };
                  });
                } catch (error) {
                  console.error(`Error fetching instances for group play ${groupPlay.id}:`, error);
                }
              })
            );
          })
          .catch((error) => console.error('Error fetching group plays:', error)),

        // 3. Fetch coaching programs then their instances in parallel
        coachingProgramAPI.getCoachingPrograms({ startDate: dateString, endDate: dateString })
          .then(async (programsResponse) => {
            const programs = programsResponse.data || [];
            await Promise.all(
              programs.map(async (program: any) => {
                try {
                  const instancesResponse = await coachingProgramAPI.getProgramInstances(program.id, {
                    startDate: dateString,
                    endDate: dateString
                  });
                  (instancesResponse.data || []).forEach((instance: any) => {
                    if (instance.status !== 'CANCELLED') validIds.add(instance.id);
                  });
                } catch (error) {
                  console.error(`Error fetching instances for program ${program.id}:`, error);
                }
              })
            );
          })
          .catch((error) => console.error('Error fetching coaching programs:', error)),
      ]);
    } catch (error) {
      console.error('Error fetching entity IDs:', error);
      // Continue even if there's an error - just won't show validation icons
    }

    return { validIds, groupPlayMap };
  };

  const fetchCalendarData = async () => {
    setLoading(true);
    setError(null);

    try {
      const dateString = formatDateForInput(selectedDate);
      const slotsData: { [amenityId: string]: AmenitySlot[] } = {};
      const slotDurations: { [amenityId: string]: number } = {};
      const outOfScheduleSlotsData: { [amenityId: string]: AmenitySlot[] } = {};

      const isAdmin = user && ['super_admin', 'admin', 'employee', 'coach'].includes(user.role);

      // Run entity ID validation and amenity slot/schedule fetching in parallel
      await Promise.all([
        // Fetch entity data for all users (group play map) and additionally for admins (validation icons)
        fetchValidEntityIds(dateString).then(({ validIds, groupPlayMap }) => {
          setGroupPlayInstanceMap(groupPlayMap);
          if (isAdmin) setValidEntityIds(validIds);
        }),

        // Fetch slots and schedules for each selected amenity
        Promise.all(
          selectedAmenities.map(async (amenity) => {
            try {
              // Fetch available slots and schedules in parallel (they're independent)
              const [slotsResult, schedulesResult] = await Promise.allSettled([
                amenityScheduleAPI.getAvailableSlots(amenity.id, dateString),
                amenityScheduleAPI.getAmenitySchedules(amenity.id)
              ]);

              // Handle slots
              if (slotsResult.status === 'fulfilled') {
                const slots = slotsResult.value.data?.slots || slotsResult.value.data || [];
                // Keep all slots including out-of-schedule in the main calendar
                // Filter out only UNAVAILABLE slots
                slotsData[amenity.id] = Array.isArray(slots)
                  ? slots.filter((slot: any) => slot.status && slot.status !== 'UNAVAILABLE')
                  : [];
              } else {
                console.error(`Error fetching slots for amenity ${amenity.id}:`, slotsResult.reason);
                slotsData[amenity.id] = [];
              }

              // Handle schedules
              if (schedulesResult.status === 'fulfilled') {
                const schedules = schedulesResult.value.data || [];
                const activeSchedule = schedules.find((s: any) => s.active);
                slotDurations[amenity.id] = activeSchedule?.minBookingDurationMinutes || 30;
              } else {
                console.error(`Error fetching schedules for amenity ${amenity.id}:`, schedulesResult.reason);
                slotDurations[amenity.id] = 30; // Default to 30 minutes
              }
            } catch (error) {
              console.error(`Error fetching data for amenity ${amenity.id}:`, error);
              slotsData[amenity.id] = [];
              slotDurations[amenity.id] = 30; // Default to 30 minutes
            }
          })
        )
      ]);

      setAmenitySlots(slotsData);
      setAmenitySlotDurations(slotDurations);
      // Fetch member profiles for all member guests
      const newGuestProfiles = await fetchGuestMemberProfiles(slotsData);

      // If in search mode, re-run the search with the new data
      if (searchMode && searchQuery.trim()) {
        const { matchingIds, resultsCount } = performSearch(
          searchQuery, 
          searchAmenities, 
          slotsData,
          newGuestProfiles
        );
        setMatchingBookingIds(matchingIds);
        setSearchResultsCount(resultsCount);
      }
    } catch (error: any) {
      console.error('Error fetching calendar data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch calendar data';
      setError(errorMessage);
      setAmenitySlots({});
      setAmenitySlotDurations({});
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const openDatePickerDialog = () => {
    setTempSelectedDate(formatDateForInput(selectedDate));
    setOpenDatePicker(true);
  };

  const handleDatePickerConfirm = () => {
    if (tempSelectedDate) {
      const newDate = parseDateOnlyString(tempSelectedDate);
      setSelectedDate(newDate);
    }
    setOpenDatePicker(false);
  };

  const handleDatePickerCancel = () => {
    setOpenDatePicker(false);
    setTempSelectedDate('');
  };

  const isToday = (): boolean => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  const scrollToCurrentTime = () => {
    if (!calendarContainerRef.current) return;

    const now = new Date();
    const currentTimeString = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Try to find an element with the current time
    const timeElement = calendarContainerRef.current.querySelector(`[data-time="${currentTimeString.substring(0, 5)}"]`);
    if (timeElement) {
      const blockElement = timeElement.parentElement;
      if (blockElement) {
        const containerRect = calendarContainerRef.current.getBoundingClientRect();
        const blockRect = blockElement.getBoundingClientRect();
        const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

        calendarContainerRef.current.scrollTo({
          top: Math.max(0, scrollTop - 100),
          behavior: 'smooth'
        });
      }
    }
  };

  const scrollToTime = (timeString: string) => {
    if (!calendarContainerRef.current) return;

    // Format time to HH:MM if needed
    let formattedTime = timeString;
    if (timeString.length === 5) {
      formattedTime = timeString; // Already in HH:MM format
    }

    // Try to find an element with the specified time
    const timeElement = calendarContainerRef.current.querySelector(`[data-time="${formattedTime}"]`);
    if (timeElement) {
      const blockElement = timeElement.parentElement;
      if (blockElement) {
        const containerRect = calendarContainerRef.current.getBoundingClientRect();
        const blockRect = blockElement.getBoundingClientRect();
        const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

        calendarContainerRef.current.scrollTo({
          top: Math.max(0, scrollTop - 100),
          behavior: 'smooth'
        });
      }
    }
  };

  const getSlotStatusColor = (status: string) => {
    const statusUpper = status?.toUpperCase().replace(/\s+/g, '_');
    
    switch (statusUpper) {
      case 'AVAILABLE': return '#c8e6c9'; // Light green
      case 'BOOKED': return '#ffcdd2'; // Light red/pink
      case 'ON_HOLD': return '#ffe0b2'; // Light orange
      case 'OUT_OF_SCHEDULE': return '#e0e0e0'; // Gray for out of schedule
      case 'UNAVAILABLE': return '#f5f5f5'; // Light gray
      default: return '#f5f5f5';
    }
  };

  const getSlotTextColor = () => {
    return '#333333'; // Dark text for light backgrounds
  };

  // Helper function to calculate GCD (Greatest Common Divisor)
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  const generateTimeBlocks = () => {
    // Calculate block interval using GCD approach to handle different slot durations
    // GCD(30, slotDuration) ensures we can display all slots properly:
    // - 30 min slots: GCD(30, 30) = 30 (blocks every 30 min)
    // - 45 min slots: GCD(30, 45) = 15 (blocks every 15 min to accommodate both)
    // - 60 min slots: GCD(30, 60) = 30 (blocks every 30 min)
    let blockInterval = 30; // Default
    if (Object.keys(amenitySlotDurations).length > 0) {
      const durations = Object.values(amenitySlotDurations);
      // For each duration, calculate GCD with 30, then find GCD of all results
      blockInterval = durations.reduce((acc, duration) => gcd(acc, gcd(30, duration)), 30);
    }

    const blocks = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += blockInterval) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        blocks.push({ time, displayTime });
      }
    }
    return blocks;
  };

  const getSlotsForTimeSlot = (amenityId: string, timeSlot: string): AmenitySlot[] => {
    const slots = amenitySlots[amenityId] || [];
    return slots.filter(slot => {
      if (!slot.startTime || !slot.endTime) return false;
      
      // Parse time for comparison - convert to minutes since midnight
      const timeToMinutes = (time: string): number => {
        const [hours, minutes] = time.substring(0, 5).split(':').map(Number);
        return hours * 60 + minutes;
      };
      
      const blockStartMinutes = timeToMinutes(timeSlot);
      const slotStartMinutes = timeToMinutes(slot.startTime);
      
      // Only show slot at its start time to avoid duplicates
      if (blockStartMinutes !== slotStartMinutes) return false;

      // If in search mode, only show matching bookings
      if (searchMode) {
        // Hide available slots in search mode
        if (!slot.bookingId) return false;
        // Only show bookings that match the search
        return matchingBookingIds.has(slot.bookingId);
      }

      return true;
    });
  };

  const getSlotSpan = (slot: AmenitySlot): number => {
    // Calculate how many time blocks this slot spans
    const timeToMinutes = (time: string): number => {
      const [hours, minutes] = time.substring(0, 5).split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const slotStartMinutes = timeToMinutes(slot.startTime);
    const slotEndMinutes = timeToMinutes(slot.endTime);
    const slotDurationMinutes = slotEndMinutes - slotStartMinutes;
    
    // Calculate block interval the same way as generateTimeBlocks
    let blockInterval = 30;
    if (Object.keys(amenitySlotDurations).length > 0) {
      const durations = Object.values(amenitySlotDurations);
      blockInterval = durations.reduce((acc, duration) => gcd(acc, gcd(30, duration)), 30);
    }
    
    return Math.ceil(slotDurationMinutes / blockInterval);
  };

  const handleSlotClick = (amenityId: string, slot: AmenitySlot) => {
    const statusUpper = slot.status?.toUpperCase().replace(/\s+/g, '_');
    
    // Handle booked, on-hold, and out-of-schedule bookings (all should navigate to detail page)
    if (slot.bookingId && (statusUpper === 'BOOKED' || statusUpper === 'OUT_OF_SCHEDULE' || statusUpper === 'ON_HOLD')) {
      // Get selected member profile from localStorage
      const selectedMemberProfileStr = localStorage.getItem('selectedMemberProfile');
      let selectedMemberProfileId: string | null = null;
      
      if (selectedMemberProfileStr) {
        try {
          const selectedMemberProfile = JSON.parse(selectedMemberProfileStr);
          selectedMemberProfileId = selectedMemberProfile?.id || null;
        } catch (e) {
          console.error('Error parsing selectedMemberProfile:', e);
        }
      }
      
      // Check if user is admin or coach
      const isAdminOrCoach = user && ['super_admin', 'admin', 'employee', 'coach'].includes(user.role);
      
      // Navigate if user is admin/coach OR if the booking belongs to the current user's selected member profile
      if (isAdminOrCoach || (slot.memberId && selectedMemberProfileId && slot.memberId === selectedMemberProfileId)) {
        navigate(`/amenities/${amenityId}/bookings/${slot.bookingId}`);
      }
      // Do nothing if the booking belongs to another user
    }
    // If slot is available, check user role
    else if (statusUpper === 'AVAILABLE') {
      const amenity = availableAmenities.find(a => a.id === amenityId);
      if (amenity) {
        // Check if user is admin or coach
        const isAdminOrCoach = user && ['super_admin', 'admin', 'employee', 'coach'].includes(user.role);
        
        if (isAdminOrCoach) {
          // Show choice dialog for admin/coach
          setChoiceDialogAmenity(amenity);
          setChoiceDialogDate(selectedDate);
          setChoiceDialogSlot({ startTime: slot.startTime, endTime: slot.endTime });
          setOpenChoiceDialog(true);
        } else {
          // Regular member - open booking dialog directly
          setBookingDialogAmenity(amenity);
          setBookingDialogDate(selectedDate);
          setBookingDialogSlot({ startTime: slot.startTime, endTime: slot.endTime });
          setOpenBookingDialog(true);
        }
      }
    }
  };

  const handleAmenityChange = (event: any, newValue: Amenity[]) => {
    setSelectedAmenities(newValue);
    
    // Save selection to localStorage
    const tenantId = localStorage.getItem('tenantId');
    const storageKey = `courtsCalendar_selectedAmenities_${tenantId}`;
    const amenityIds = newValue.map(a => a.id);
    localStorage.setItem(storageKey, JSON.stringify(amenityIds));
  };

  const handleBookForMember = () => {
    // Close choice dialog and open booking dialog
    setOpenChoiceDialog(false);
    setBookingDialogAmenity(choiceDialogAmenity);
    setBookingDialogDate(choiceDialogDate);
    setBookingDialogSlot(choiceDialogSlot);
    setOpenBookingDialog(true);
  };

  const handleBookCoachingSession = () => {
    // Close choice dialog and open create coaching session dialog
    setOpenChoiceDialog(false);
    setCreateSessionDialogAmenity(choiceDialogAmenity);
    setCreateSessionDialogDate(choiceDialogDate);
    setCreateSessionDialogSlot(choiceDialogSlot);
    setOpenCreateSessionDialog(true);
  };

  const handleReserveAmenity = () => {
    // Close choice dialog and open reserve dialog
    setOpenChoiceDialog(false);
    setReserveDialogAmenity(choiceDialogAmenity);
    setReserveDialogDate(choiceDialogDate);
    setReserveDialogSlot(choiceDialogSlot);
    setOpenReserveDialog(true);
  };

  const handleChoiceDialogClose = () => {
    setOpenChoiceDialog(false);
    setChoiceDialogAmenity(null);
    setChoiceDialogDate(null);
    setChoiceDialogSlot(null);
  };

  const handleOpenSearchDialog = () => {
    setSearchAmenities(selectedAmenities);
    setOpenSearchDialog(true);
  };

  const handleCloseSearchDialog = () => {
    setOpenSearchDialog(false);
  };

  // Perform search on current amenity slots
  const performSearch = (
    query: string, 
    amenitiesToSearch: Amenity[], 
    slotsData: { [amenityId: string]: AmenitySlot[] },
    guestProfiles: { [memberId: string]: any }
  ) => {
    const queryLower = query.toLowerCase().trim();
    const matchingIds = new Set<string>();
    let resultsCount = 0;

    // Search through amenity slots for matching bookings
    amenitiesToSearch.forEach(amenity => {
      const slots = slotsData[amenity.id] || [];
      slots.forEach(slot => {
        let hasMatch = false;

        // Check host name and email
        if (slot.bookedBy && slot.bookedBy.toLowerCase().includes(queryLower)) {
          hasMatch = true;
        }
        if (slot.bookedByEmail && slot.bookedByEmail.toLowerCase().includes(queryLower)) {
          hasMatch = true;
        }

        // Check all bookings in the slot (for shared slots)
        if (slot.allBookings && slot.allBookings.length > 0) {
          slot.allBookings.forEach(booking => {
            // Check booking member name
            if (booking.memberName && booking.memberName.toLowerCase().includes(queryLower)) {
              hasMatch = true;
            }

            // Check guests
            if (booking.guests && booking.guests.length > 0) {
              booking.guests.forEach(guest => {
                if (guest.type === 'member' && guest.memberId) {
                  const memberProfile = guestProfiles[guest.memberId];
                  if (memberProfile) {
                    const guestName = `${memberProfile.firstName || ''} ${memberProfile.lastName || ''}`.toLowerCase();
                    const guestEmail = memberProfile.email?.toLowerCase() || '';
                    if (guestName.includes(queryLower) || guestEmail.includes(queryLower)) {
                      hasMatch = true;
                    }
                  }
                } else if (guest.type === 'guest' && guest.guest) {
                  const guestName = guest.guest.name?.toLowerCase() || '';
                  const guestEmail = guest.guest.email?.toLowerCase() || '';
                  if (guestName.includes(queryLower) || guestEmail.includes(queryLower)) {
                    hasMatch = true;
                  }
                }
              });
            }
          });
        }

        if (hasMatch && slot.bookingId) {
          matchingIds.add(slot.bookingId);
          resultsCount++;
        }
      });
    });

    return { matchingIds, resultsCount };
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      return;
    }

    const { matchingIds, resultsCount } = performSearch(
      searchQuery, 
      searchAmenities, 
      amenitySlots,
      guestMemberProfiles
    );

    setMatchingBookingIds(matchingIds);
    setSearchResultsCount(resultsCount);
    setSearchMode(true);
    setOpenSearchDialog(false);
  };

  const handleBackFromSearch = () => {
    setSearchMode(false);
    setSearchQuery('');
    setMatchingBookingIds(new Set());
    setSearchResultsCount(0);
  };

  const isBookingOrphaned = (slot: AmenitySlot): boolean => {
    // A booking is orphaned if it references a coaching session, group play instance,
    // or coaching program instance that doesn't exist or is cancelled
    
    // Check if slot has a coaching session or group play instance reference
    // (both use coachingSessionId in additionalInfo)
    if (slot.coachingSessionId) {
      return !validEntityIds.has(slot.coachingSessionId);
    }
    
    // Check if slot has a coaching program instance reference
    if (slot.programInstanceId) {
      return !validEntityIds.has(slot.programInstanceId);
    }
    
    return false;
  };

  const getTooltipContent = (amenity: Amenity, slot: AmenitySlot) => {
    const isAdmin = user && ['super_admin', 'admin', 'employee', 'coach'].includes(user.role);
    const groupPlayInfo = slot.coachingSessionId ? groupPlayInstanceMap[slot.coachingSessionId] : undefined;
    
    // Collect all guests from all bookings in this slot
    const allGuests: BookingGuest[] = [];
    if (slot.allBookings && slot.allBookings.length > 0) {
      slot.allBookings.forEach(booking => {
        if (booking.guests && booking.guests.length > 0) {
          allGuests.push(...booking.guests);
        }
      });
    }

    // Calculate total players (host + guests)
    const hasHost = slot.bookedBy ? 1 : 0;
    const totalPlayers = hasHost + allGuests.length;

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {amenity.name}
        </Typography>
        <Typography variant="body2">
          Status: {slot.status}
        </Typography>
        <Typography variant="body2">
          Time: {slot.startTime} - {slot.endTime}
        </Typography>
        {groupPlayInfo ? (
          <>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>Group Play:</strong> {groupPlayInfo.name}
            </Typography>
            {groupPlayInfo.coachName && (
              <Typography variant="body2">
                <strong>Coach:</strong> {groupPlayInfo.coachName}
              </Typography>
            )}
          </>
        ) : (
          slot.bookedBy && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>Host:</strong> {slot.bookedBy}
            </Typography>
          )
        )}
        {allGuests.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Players:
            </Typography>
            {allGuests.map((guest, index) => {
              if (guest.type === 'member' && guest.memberId) {
                // For member guests, show their name from the loaded profile
                const memberProfile = guestMemberProfiles[guest.memberId];
                return (
                  <Typography key={index} variant="body2" sx={{ ml: 1, fontSize: '0.8125rem' }}>
                    {memberProfile 
                        ? `${memberProfile.firstName} ${memberProfile.lastName}` 
                        : `Member (ID: ${guest.memberId.substring(0, 8)}...)`}
                  </Typography>
                );
              } else if (guest.type === 'guest' && guest.guest) {
                // For external guests, show name stored in the booking
                return (
                  <Typography key={index} variant="body2" sx={{ ml: 1, fontSize: '0.8125rem' }}>
                    {guest.guest.name || 'Guest'}
                  </Typography>
                );
              }
              return null;
            })}
          </Box>
        )}
        {!groupPlayInfo && slot.coachingSessionName && (
          <Typography variant="body2">
            Coaching Session: {slot.coachingSessionName}
            {isAdmin && slot.coachingSessionStatus?.toUpperCase() === 'CANCELLED' && (
              <Chip 
                label="CANCELLED" 
                size="small" 
                color="warning" 
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
              />
            )}
          </Typography>
        )}
        {slot.programName && (
          <Typography variant="body2">
            Program: {slot.programName}
            {isAdmin && slot.programInstanceStatus?.toUpperCase() === 'CANCELLED' && (
              <Chip 
                label="CANCELLED" 
                size="small" 
                color="warning" 
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
              />
            )}
          </Typography>
        )}
        {slot.pricingTier && (
          <Typography variant="body2">
            Pricing: {slot.pricingTier.charAt(0).toUpperCase() + slot.pricingTier.slice(1).replace('-', ' ')}
          </Typography>
        )}
      </Box>
    );
  };

  const renderDailyView = () => {
    const timeBlocks = generateTimeBlocks();
    
    // Calculate block interval the same way as generateTimeBlocks
    let blockInterval = 30;
    if (Object.keys(amenitySlotDurations).length > 0) {
      const durations = Object.values(amenitySlotDurations);
      blockInterval = durations.reduce((acc, duration) => gcd(acc, gcd(30, duration)), 30);
    }

    // Create a set of time block indices that have scheduled slots for any amenity
    const occupiedSlots = new Set<number>();
    
    // Use search amenities if in search mode, otherwise use selected amenities
    const amenitiesToShow = searchMode ? searchAmenities : selectedAmenities;
    
    if (amenitiesToShow.length > 0) {
      timeBlocks.forEach((block, index) => {
        // Check if any amenity has a slot at this time
        const hasSlots = amenitiesToShow.some(amenity => {
          const slots = getSlotsForTimeSlot(amenity.id, block.time);
          return slots.length > 0;
        });
        
        if (hasSlots) {
          // Mark this slot as occupied
          occupiedSlots.add(index);
          
          // Also mark subsequent slots if any slot spans multiple blocks
          amenitiesToShow.forEach(amenity => {
            const slots = getSlotsForTimeSlot(amenity.id, block.time);
            slots.forEach(slot => {
              const span = getSlotSpan(slot);
              for (let i = 1; i < span; i++) {
                occupiedSlots.add(index + i);
              }
            });
          });
        }
      });
    }

    if (amenitiesToShow.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Please select at least one amenity to view the calendar
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        ref={calendarContainerRef}
        sx={{
          border: { xs: 0, sm: 1 },
          borderColor: 'divider',
          borderRadius: { xs: 0, sm: 1 },
          overflow: 'auto',
          boxShadow: { xs: 0, sm: 1 },
          maxHeight: { xs: '75vh', sm: '70vh' }
        }}
      >
        {/* Header with amenity names */}
        <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper' }}>
          <Box
            sx={{
              width: { xs: 50, sm: 80 },
              p: { xs: 0.5, sm: 1 },
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              Time
            </Typography>
          </Box>
          {(searchMode ? searchAmenities : selectedAmenities).map((amenity, index) => (
            <Box
              key={amenity.id}
              sx={{
                minWidth: { xs: 120, sm: 180 },
                flex: 1,
                p: { xs: 0.5, sm: 1 },
                borderRight: index < (searchMode ? searchAmenities : selectedAmenities).length - 1 ? 1 : 0,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'grey.100',
                textAlign: 'center'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {amenity.name}
              </Typography>
              {amenity.type && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  {amenity.type}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Time slots */}
        {timeBlocks
          .filter((block, index) => {
            // Only show slots that are occupied by scheduled slots
            return occupiedSlots.has(index);
          })
          .map((block, blockIndex) => (
          <Box
            key={blockIndex}
            sx={{
              display: 'flex',
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: { xs: 50, sm: 60 },
              '&:hover': { bgcolor: 'grey.50' }
            }}
          >
            <Box
              sx={{
                width: { xs: 50, sm: 80 },
                p: { xs: 0.5, sm: 1 },
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                data-time={block.time}
              >
                {block.displayTime}
              </Typography>
            </Box>
            {(searchMode ? searchAmenities : selectedAmenities).map((amenity, amenityIndex) => (
              <Box
                key={amenity.id}
                sx={{
                  minWidth: { xs: 120, sm: 180 },
                  flex: 1,
                  p: { xs: 0.25, sm: 0.5 },
                  borderRight: amenityIndex < (searchMode ? searchAmenities : selectedAmenities).length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  position: 'relative'
                }}
              >
                {getSlotsForTimeSlot(amenity.id, block.time).map((slot, slotIndex) => {
                  const slotSpan = getSlotSpan(slot);
                  const baseBlockHeight = 50; // Mobile/desktop responsive height handled by minHeight in sx
                  const slotHeight = slotSpan * baseBlockHeight;
                  const isAdmin = user && ['super_admin', 'admin', 'employee', 'coach'].includes(user.role);
                  const isCoachingRelated = !!(slot.coachingSessionId || slot.programInstanceId);
                  const orphaned = isAdmin && isCoachingRelated ? isBookingOrphaned(slot) : false;
                  const isProgramCancelled = slot.programInstanceStatus?.toUpperCase() === 'CANCELLED';
                  const isSessionCancelled = slot.coachingSessionStatus?.toUpperCase() === 'CANCELLED';
                  
                  return (
                    <Tooltip
                      key={slotIndex}
                      title={getTooltipContent(amenity, slot)}
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 300,
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            color: 'white',
                            fontSize: '0.875rem',
                            padding: 2
                          }
                        }
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 2, sm: 4 },
                          left: { xs: 2, sm: 4 },
                          right: { xs: 2, sm: 4 },
                          height: `${slotHeight - 8}px`,
                          bgcolor: getSlotStatusColor(slot.status),
                          color: getSlotTextColor(),
                          cursor: 'pointer',
                          borderRadius: 1,
                          p: { xs: 0.5, sm: 1 },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-start',
                          zIndex: 1,
                          '&:hover': {
                            boxShadow: 2,
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out',
                            zIndex: 2
                          }
                        }}
                        onClick={() => handleSlotClick(amenity.id, slot)}
                      >
                        <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, lineHeight: 1.2, fontWeight: 'bold' }}>
                          {slot.startTime} - {slot.endTime}
                          {slot.pricingTier && (
                            <> ({slot.pricingTier.charAt(0).toUpperCase() + slot.pricingTier.slice(1).replace('-', ' ')})</>
                          )}
                        </Typography>
                        {slot.coachingSessionId && groupPlayInstanceMap[slot.coachingSessionId] ? (
                          <>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: { xs: '0.55rem', sm: '0.65rem' },
                                lineHeight: 1.2,
                                mt: 0.25,
                                fontWeight: 'bold',
                                display: 'block',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                flexShrink: 0
                              }}
                            >
                              Group Play: {groupPlayInstanceMap[slot.coachingSessionId].name}
                            </Typography>

                          </>
                        ) : (
                          <>
                            {slot.bookedBy && !slot.coachingSessionName && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                                  lineHeight: 1.2,
                                  mt: 0.25,
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flexShrink: 0
                                }}
                              >
                                {slot.maintenanceDescription
                                  ? slot.maintenanceDescription
                                  : `Host: ${slot.bookedBy}`}
                              </Typography>
                            )}
                            {slot.coachingSessionName && (
                              <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, lineHeight: 1.2, mt: 0.25, fontStyle: 'italic', opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
                                Session: {slot.coachingSessionName}
                              </Typography>
                            )}
                          </>
                        )}
                        {slot.programName && (
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, lineHeight: 1.2, mt: 0.25, fontStyle: 'italic', opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
                            Program: {slot.programName}
                          </Typography>
                        )}
                        {/* Equipment icon - top right, shown when booking has equipment */}
                        {slot.equipment && slot.equipment.length > 0 && (
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Equipment
                                </Typography>
                                {slot.equipment.map((eq, eqIndex) => (
                                  <Typography key={eqIndex} variant="body2" sx={{ fontSize: '0.8125rem' }}>
                                    {eq.name} &times; {eq.quantity} &mdash; {slot.startTime.substring(0, 5)} &ndash; {slot.endTime.substring(0, 5)}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 260,
                                  bgcolor: 'rgba(0, 0, 0, 0.9)',
                                  color: 'white',
                                  fontSize: '0.875rem',
                                  padding: 1.5
                                }
                              }
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                top: { xs: 2, sm: 3 },
                                right: { xs: 2, sm: 3 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 3
                              }}
                              onMouseEnter={(e) => e.stopPropagation()}
                            >
                              <LocalActivityIcon
                                sx={{
                                  fontSize: { xs: '0.85rem', sm: '1rem' },
                                  color: 'primary.main',
                                  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.25))'
                                }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                        {/* Warning icon for admins on orphaned/cancelled coaching-related bookings */}
                        {isAdmin && isCoachingRelated && (orphaned || isProgramCancelled || isSessionCancelled) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: { xs: 2, sm: 4 },
                              left: { xs: 2, sm: 4 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 2
                            }}
                          >
                            <WarningIcon 
                              sx={{ 
                                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                color: 'warning.main',
                                filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))'
                              }} 
                            />
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const getViewTitle = () => {
    return selectedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  return (
    <Container maxWidth="xl" sx={{ px: 0 }}>
      <Box sx={{ 
        mt: { xs: 0, sm: 2 }, 
        mb: { xs: 1, sm: 2 }, 
        px: { xs: 0, sm: 0 },
        mx: { xs: -2, sm: 0 }
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 2 }, gap: 1, px: { xs: 1, sm: 0 } }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            <CalendarTodayIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            Courts Calendar
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {searchMode ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                onClick={handleBackFromSearch}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
              >
                Back
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<SearchIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                onClick={handleOpenSearchDialog}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
              >
                Search
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={fetchCalendarData}
              disabled={loading}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Controls */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 1, sm: 2 }, mx: { xs: 1, sm: 0 } }}>
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            {/* Amenity Multi-Select */}
            <Box>
              <Autocomplete
                multiple
                id="amenities-select"
                options={availableAmenities}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                value={selectedAmenities}
                onChange={handleAmenityChange}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                    {option.type && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({option.type})
                      </Typography>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Amenities"
                    placeholder="Choose amenities to display"
                    size="small"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.name}
                        {...chipProps}
                        size="small"
                        sx={{ maxWidth: { xs: '100px', sm: '150px' } }}
                      />
                    );
                  })
                }
                limitTags={2}
                sx={{
                  '& .MuiAutocomplete-tag': {
                    maxWidth: { xs: '100px', sm: '150px' }
                  }
                }}
              />
            </Box>

            {/* Date Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
              <IconButton onClick={() => navigateDate('prev')} size="small" sx={{ p: { xs: 0.5, sm: 1 } }}>
                <ChevronLeftIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              </IconButton>
              <Typography 
                variant="body1" 
                sx={{ 
                  minWidth: { xs: 'auto', sm: 180 }, 
                  textAlign: 'center',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 500
                }}
              >
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
              </Typography>
              <IconButton onClick={() => navigateDate('next')} size="small" sx={{ p: { xs: 0.5, sm: 1 } }}>
                <ChevronRightIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                onClick={goToToday}
                startIcon={<TodayIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                disabled={isToday()}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 1.5 }, minWidth: { xs: 'auto', sm: 'auto' } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Today</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>T</Box>
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={openDatePickerDialog}
                startIcon={<DateRangeIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 1.5 }, minWidth: { xs: 'auto', sm: 'auto' } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Jump</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>J</Box>
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, mx: { xs: 1, sm: 0 } }}>
            {error}
          </Alert>
        )}

        {/* Search Results Banner */}
        {searchMode && (
          <Alert 
            severity={searchResultsCount > 0 ? "info" : "warning"} 
            sx={{ mb: 2, mx: { xs: 1, sm: 0 } }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleBackFromSearch}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {searchResultsCount > 0 ? (
              <Typography variant="body2">
                <strong>Showing filtered results for: "{searchQuery}"</strong> - Found {searchResultsCount} booking{searchResultsCount !== 1 ? 's' : ''}
              </Typography>
            ) : (
              <Typography variant="body2">
                <strong>No results found matching your search</strong>
              </Typography>
            )}
          </Alert>
        )}

        {/* Legend */}
        <Paper sx={{ p: { xs: 1, sm: 1.5 }, mb: { xs: 1, sm: 2 }, bgcolor: 'grey.50', mx: { xs: 1, sm: 0 } }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip label="Available" size="small" sx={{ bgcolor: '#c8e6c9', color: '#333', fontSize: { xs: '0.7rem', sm: '0.8125rem' } }} />
            <Chip label="Booked" size="small" sx={{ bgcolor: '#ffcdd2', color: '#333', fontSize: { xs: '0.7rem', sm: '0.8125rem' } }} />
            <Chip label="On Hold" size="small" sx={{ bgcolor: '#ffe0b2', color: '#333', fontSize: { xs: '0.7rem', sm: '0.8125rem' } }} />
          </Stack>
        </Paper>

        {/* Calendar Content */}
        <Paper sx={{ p: { xs: 0, sm: 1 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
              <CircularProgress />
            </Box>
          ) : (
            renderDailyView()
          )}
        </Paper>

        {/* Out of Schedule Bookings Section */}
        {!loading && Object.values(outOfScheduleBookings).some(slots => slots.length > 0) && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 2, bgcolor: 'grey.100', mx: { xs: 1, sm: 0 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.125rem' } }}>
              Out of Schedule Bookings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These bookings were made outside of the regular schedule hours
            </Typography>
            <Stack spacing={1}>
              {(searchMode ? searchAmenities : selectedAmenities).map((amenity) => {
                const slots = outOfScheduleBookings[amenity.id] || [];
                if (slots.length === 0) return null;
                
                return (
                  <Box key={amenity.id}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {amenity.name}
                    </Typography>
                    <Stack spacing={0.5}>
                      {slots.map((slot, index) => {
                        // For OUT_OF_SCHEDULE slots with multiple bookings, use first booking's ID
                        const bookingId = slot.bookingId || (slot.allBookings && slot.allBookings.length > 0 ? slot.allBookings[0].id : null);
                        
                        return (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              bgcolor: 'white',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              cursor: bookingId ? 'pointer' : 'default',
                              '&:hover': bookingId ? {
                                boxShadow: 2,
                                borderColor: 'grey.400',
                                transform: 'translateY(-1px)',
                                transition: 'all 0.2s ease-in-out'
                              } : {}
                            }}
                            onClick={(e) => {
                              if (bookingId) {
                                navigate(`/amenities/${amenity.id}/bookings/${bookingId}`);
                              }
                            }}
                          >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {slot.startTime} - {slot.endTime}
                              </Typography>
                              {slot.coachingSessionId && groupPlayInstanceMap[slot.coachingSessionId] ? (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    {groupPlayInstanceMap[slot.coachingSessionId].name}
                                  </Typography>
                                  {groupPlayInstanceMap[slot.coachingSessionId].coachName && (
                                    <Typography variant="body2" color="text.secondary">
                                      Coach: {groupPlayInstanceMap[slot.coachingSessionId].coachName}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                slot.bookedBy && (
                                  <Typography variant="body2" color="text.secondary">
                                    Host: {slot.bookedBy}
                                  </Typography>
                                )
                              )}
                              {slot.allBookings && slot.allBookings.length > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {slot.allBookings.length} booking{slot.allBookings.length !== 1 ? 's' : ''}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label="Out of Schedule"
                              size="small"
                              sx={{ bgcolor: 'grey.400', color: 'white' }}
                            />
                          </Box>
                        </Box>
                      );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Date Picker Dialog */}
        <Dialog open={openDatePicker} onClose={handleDatePickerCancel} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DateRangeIcon sx={{ mr: 1 }} />
              Jump to Date
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Select Date"
                type="date"
                value={tempSelectedDate}
                onChange={(e) => setTempSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDatePickerCancel}>Cancel</Button>
            <Button
              onClick={handleDatePickerConfirm}
              variant="contained"
              disabled={!tempSelectedDate}
            >
              Go to Date
            </Button>
          </DialogActions>
        </Dialog>

        {/* Search Dialog */}
        <Dialog 
          open={openSearchDialog} 
          onClose={handleCloseSearchDialog} 
          maxWidth="sm" 
          fullWidth
          disableRestoreFocus
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SearchIcon sx={{ mr: 1 }} />
              Search Bookings
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Search by name or email"
                placeholder="Enter member, host, or guest name/email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
              <Autocomplete
                multiple
                options={availableAmenities}
                value={searchAmenities}
                onChange={(event, newValue) => setSearchAmenities(newValue)}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option, { selected }) => {
                  const { key, ...restProps } = props as any;
                  return (
                    <li key={key} {...restProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Amenities" placeholder="Select amenities to search" />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSearchDialog}>Cancel</Button>
            <Button 
              onClick={handleSearch} 
              variant="contained"
              disabled={!searchQuery.trim() || searchAmenities.length === 0}
            >
              Search
            </Button>
          </DialogActions>
        </Dialog>

        {/* Choice Dialog for Admin/Coach */}
        <Dialog 
          open={openChoiceDialog} 
          onClose={handleChoiceDialogClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Select Booking Type</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                How would you like to use this time slot?
              </Typography>
              {choiceDialogAmenity && choiceDialogSlot && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Amenity:</strong> {choiceDialogAmenity.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Time:</strong> {formatTimeString(choiceDialogSlot.startTime)} - {formatTimeString(choiceDialogSlot.endTime)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date:</strong> {choiceDialogDate?.toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2, pt: 0 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleBookForMember}
              startIcon={<PersonIcon />}
            >
              Book for Member
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={handleBookCoachingSession}
              startIcon={<CalendarTodayIcon />}
            >
              Create Coaching Session
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleReserveAmenity}
              startIcon={<CalendarTodayIcon />}
            >
              Reserve (Hold)
            </Button>
            <Button
              fullWidth
              onClick={handleChoiceDialogClose}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Booking Dialog */}
        <BookAmenityDialog
          open={openBookingDialog}
          onClose={() => {
            setOpenBookingDialog(false);
            setBookingDialogAmenity(null);
            setBookingDialogDate(null);
            setBookingDialogSlot(null);
          }}
          amenity={bookingDialogAmenity}
          onSuccess={(message) => {
            setSuccessMessage(message || 'Booking created successfully!');
            // Refresh calendar data to show the new booking
            fetchCalendarData();
          }}
          preselectedDate={bookingDialogDate}
          preselectedSlot={bookingDialogSlot}
        />

        {/* Reserve Amenity Dialog */}
        <ReserveAmenityDialog
          open={openReserveDialog}
          onClose={() => {
            setOpenReserveDialog(false);
            setReserveDialogAmenity(null);
            setReserveDialogDate(null);
            setReserveDialogSlot(null);
          }}
          amenity={reserveDialogAmenity}
          onSuccess={(message) => {
            setSuccessMessage(message || 'Amenity reserved successfully!');
            // Refresh calendar data to show the new reservation
            fetchCalendarData();
          }}
          preselectedDate={reserveDialogDate}
          preselectedSlot={reserveDialogSlot}
        />

        {/* Create Coaching Session Dialog */}
        <CreateCoachingSessionDialog
          open={openCreateSessionDialog}
          onClose={() => {
            setOpenCreateSessionDialog(false);
            setCreateSessionDialogAmenity(null);
            setCreateSessionDialogDate(null);
            setCreateSessionDialogSlot(null);
          }}
          amenity={createSessionDialogAmenity}
          onSuccess={(message) => {
            setSuccessMessage(message || 'Coaching session created successfully!');
            // Refresh calendar data to show the new session
            fetchCalendarData();
          }}
          preselectedDate={createSessionDialogDate}
          preselectedSlot={createSessionDialogSlot}
        />

        {/* Success Snackbar */}
        {successMessage && (
          <Alert
            severity="success"
            onClose={() => setSuccessMessage(null)}
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          >
            {successMessage}
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default CourtsCalendar;
